import React, { useState, useEffect } from 'react';
import { Component, Platform, PlatformItem } from '../../types';
import { apiRequest } from '../../utils/api';
import { showTempMessage } from '../../utils/messages';
import MilestoneSelector from '../shared/MilestoneSelector';

interface BulkEditComponentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedComponents: Component[];
  allPlatforms: PlatformItem[];
  productId?: string;
  onSuccess: () => void;
  setMessage: (msg: any) => void;
}

const BulkEditComponentsModal: React.FC<BulkEditComponentsModalProps> = ({
  isOpen,
  onClose,
  selectedComponents,
  allPlatforms,
  productId,
  onSuccess,
  setMessage
}) => {
  const [selectedPlatforms, setSelectedPlatforms] = useState<Platform[]>([]);
  const [selectedMilestone, setSelectedMilestone] = useState<string>('');
  const [updatePlatforms, setUpdatePlatforms] = useState<boolean>(false);
  const [updateMilestone, setUpdateMilestone] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Filter platforms by productId
  // Show platforms that either:
  // 1. Have no productIds (applies to all products), OR
  // 2. Include the productId in their productIds array
  const filteredPlatforms = productId
    ? allPlatforms.filter(p => 
        !p.productIds || 
        p.productIds.length === 0 || 
        p.productIds.includes(productId)
      )
    : allPlatforms; // If no product selected, show all platforms

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedPlatforms([]);
      setSelectedMilestone('');
      setUpdatePlatforms(false);
      setUpdateMilestone(false);
    }
  }, [isOpen]);

  const handleTogglePlatform = (platform: Platform) => {
    if (selectedPlatforms.includes(platform)) {
      setSelectedPlatforms(selectedPlatforms.filter(p => p !== platform));
    } else {
      setSelectedPlatforms([...selectedPlatforms, platform]);
    }
  };

  const handleSubmit = async () => {
    if (!updatePlatforms && !updateMilestone) {
      showTempMessage(setMessage, { type: 'error', text: 'Please select at least one field to update' });
      return;
    }

    if (updatePlatforms && selectedPlatforms.length === 0) {
      showTempMessage(setMessage, { type: 'error', text: 'Please select at least one platform' });
      return;
    }

    if (updateMilestone && !selectedMilestone.trim()) {
      showTempMessage(setMessage, { type: 'error', text: 'Please select a milestone' });
      return;
    }

    setIsSubmitting(true);
    try {
      const updates: any = {};
      
      if (updatePlatforms) {
        updates.platforms = selectedPlatforms;
      }
      
      if (updateMilestone) {
        updates.milestone = selectedMilestone.trim();
      }

      const componentIds = selectedComponents.map(c => c.id);
      
      const result = await apiRequest<{
        success: boolean;
        updated: number;
        failed: number;
        components: Component[];
        errors?: Array<{ componentId: string; error: string }>;
      }>('/api/components/bulk', {
        method: 'PUT',
        body: JSON.stringify({
          componentIds,
          updates
        })
      }, { useAuth: true, setMessage });

      if (result.errors && result.errors.length > 0) {
        showTempMessage(setMessage, { 
          type: 'error', 
          text: `Updated ${result.updated} component(s), ${result.failed} failed` 
        });
      } else {
        showTempMessage(setMessage, { 
          type: 'success', 
          text: `Successfully updated ${result.updated} component(s)` 
        });
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error bulk updating components:', error);
      const errorMsg = error?.message || error?.error || 'Failed to update components';
      showTempMessage(setMessage, { type: 'error', text: errorMsg });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" style={{ zIndex: 2000 }}>
      <div
        className="card modal-content"
        style={{
          maxWidth: '600px',
          width: '90%',
          position: 'relative',
          zIndex: 2001
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2 className="title-lg" style={{ margin: 0 }}>Bulk Edit Components</h2>
          <button
            className="btn btn-ghost btn-icon"
            onClick={onClose}
            style={{ fontSize: '1.5rem', padding: '0.5rem' }}
            title="Close"
          >
            ×
          </button>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <p className="subtitle" style={{ marginBottom: '0.75rem', fontSize: '0.875rem' }}>
            Updating <strong>{selectedComponents.length}</strong> component{selectedComponents.length !== 1 ? 's' : ''}:
          </p>
          <div style={{ 
            maxHeight: '150px', 
            overflowY: 'auto', 
            padding: '0.75rem',
            backgroundColor: 'var(--surface-elevated)',
            borderRadius: '6px',
            border: '1px solid var(--grid-line)'
          }}>
            {selectedComponents.map((component, index) => (
              <div 
                key={component.id} 
                style={{ 
                  padding: '0.5rem 0',
                  borderBottom: index < selectedComponents.length - 1 ? '1px solid var(--grid-line)' : 'none',
                  fontSize: '0.875rem',
                  color: 'var(--text-primary)'
                }}
              >
                {component.name}
              </div>
            ))}
          </div>
        </div>

        {/* Platforms Section */}
        <div className="form-group" style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.75rem' }}>
            <input
              type="checkbox"
              id="updatePlatforms"
              checked={updatePlatforms}
              onChange={(e) => setUpdatePlatforms(e.target.checked)}
              style={{ marginRight: '0.5rem', cursor: 'pointer', width: '1rem', height: '1rem' }}
            />
            <label htmlFor="updatePlatforms" className="label" style={{ cursor: 'pointer', margin: 0, fontWeight: 600 }}>
              Update Platforms
            </label>
          </div>
          
          {updatePlatforms && (
            <div style={{ marginLeft: '1.5rem', marginTop: '0.5rem', paddingLeft: '0.5rem', borderLeft: '2px solid var(--grid-line)' }}>
              <p className="subtitle" style={{ fontSize: '0.875rem', marginBottom: '0.75rem' }}>
                Select platforms to add to all selected components:
              </p>
              <div className="platform-controls-group-runner" style={{ marginBottom: '0.5rem' }}>
                {filteredPlatforms.length === 0 ? (
                  <p className="hint" style={{ fontSize: '0.875rem', margin: 0 }}>
                    No platforms available for this product. Please add platforms in Settings.
                  </p>
                ) : (
                  filteredPlatforms.map(platformItem => {
                    const platform = platformItem.name as Platform;
                    const isSelected = selectedPlatforms.includes(platform);
                    return (
                      <div key={platformItem.id} className="platform-control-wrapper-runner">
                        <button
                          type="button"
                          className={`platform-tested-button ${isSelected ? 'tested' : ''}`}
                          onClick={() => handleTogglePlatform(platform)}
                          title={isSelected ? `${platform} selected (click to deselect)` : `Select ${platform}`}
                        >
                          <span className="platform-name">{platform}</span>
                          {isSelected && <span className="platform-icon">✓</span>}
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
              <p className="hint" style={{ fontSize: '0.75rem', marginTop: '0.5rem', marginBottom: 0 }}>
                Note: Selected platforms will replace existing platforms for all selected components.
              </p>
            </div>
          )}
        </div>

        {/* Milestone Section */}
        <div className="form-group" style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.75rem' }}>
            <input
              type="checkbox"
              id="updateMilestone"
              checked={updateMilestone}
              onChange={(e) => setUpdateMilestone(e.target.checked)}
              style={{ marginRight: '0.5rem', cursor: 'pointer', width: '1rem', height: '1rem' }}
            />
            <label htmlFor="updateMilestone" className="label" style={{ cursor: 'pointer', margin: 0, fontWeight: 600 }}>
              Update Milestone
            </label>
          </div>
          
          {updateMilestone && (
            <div style={{ marginLeft: '1.5rem', marginTop: '0.5rem', paddingLeft: '0.5rem', borderLeft: '2px solid var(--grid-line)' }}>
              <label className="label" style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                Milestone <span style={{ color: 'var(--cyan-glow)' }}>*</span>
              </label>
              <MilestoneSelector
                value={selectedMilestone}
                onChange={setSelectedMilestone}
                productId={productId}
                placeholder="Select or create milestone"
                required
              />
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-sm justify-end" style={{ 
          paddingTop: '1rem',
          borderTop: '1px solid var(--grid-line)',
          marginTop: '1.5rem'
        }}>
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={isSubmitting || (!updatePlatforms && !updateMilestone)}
          >
            {isSubmitting ? 'Updating...' : `Update ${selectedComponents.length} Component${selectedComponents.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkEditComponentsModal;

